/**
 * The first two minutes.
 *
 * Every empty state in this app assumed prior data. A ring with no target can
 * only count upward, so it showed a number climbing toward nothing. Review had
 * an average of zero days. Plan had nothing to plan from. The repeat
 * suggestions suggested nothing. None of that is broken, exactly — it is an app
 * that only makes sense to somebody who has already used it.
 *
 * So: confirm the zone, take the measurements the profile wants anyway, and
 * **compute the budget in front of the user** rather than asking for one.
 * Asking for a calorie target asks somebody to have already done the arithmetic
 * they downloaded a tracker to avoid, and the number they invent instead is
 * reliably too low — which is how a first week ends in giving up.
 *
 * The timezone strip is first and not buried in settings for the reason spelled
 * out in `TimezoneMismatch`: the profile is created lazily with a guessed zone,
 * a wrong guess files every evening meal on the following day forever, and this
 * is the only moment the app can catch it before any damage is done.
 *
 * Skipping is a real exit. Someone who only wants a calorie count should not be
 * held at a form about their height.
 *
 * ## Why the date of birth is a field here
 *
 * It used to be a sentence pointing at **You**. Mifflin-St Jeor needs an age,
 * nothing in the invite or the sign-in collects one, and the column is
 * nullable — so for *every* new account the estimate was null, the budget block
 * never rendered, and the screen whose entire argument is "we compute this for
 * you" computed nothing and wrote no daily target. The pointer was unreachable
 * besides: this renders in a bare `<main>` with no navigation, so the only
 * route to **You** was pressing Skip, i.e. abandoning the screen to satisfy it.
 * One date input is cheaper than all of that, and the age it yields comes from
 * the field rather than from the profile, which is the single line that makes
 * the estimate work at all.
 *
 * ## Why the fields are in this order
 *
 * The three estimator inputs and the activity level come first, so the number
 * appears before the user has thought about a target — a blank target is rate
 * zero, which is maintenance, which is a perfectly good budget. *Then* the
 * target pair, so typing a goal visibly moves a figure that is already on
 * screen. That is the screen's argument demonstrated rather than asserted, and
 * it is the reason the two halves of the grid are not one grid of six.
 *
 * `max` on the date but no `min`: a future date is accepted by the server
 * (`birth_date` has no bounds) and would make the estimate silently vanish all
 * over again, so that one is worth a hard stop. The lower bound is `ageOn`'s
 * own `0 < years < 130`, which reads better as a sentence under the field than
 * as a clamped spinner somebody has to fight.
 *
 * ## The write order, which is load-bearing twice
 *
 * 1. **One `PATCH /v1/me`** of changed keys only. It used to be two patches,
 *    the second conditional on a finite height — so choosing an activity level
 *    and leaving height blank threw the choice away. One patch, built field by
 *    field the way `ProfileCard` builds it, has no such gap.
 * 2. **`PUT /v1/weight`, before the goal, and only when the value changed.**
 *    Before, because `PUT /v1/goal` answers 400 without a weight entry to
 *    snapshot a starting point from — the screen holding the weight in its hand
 *    was the one place that had not learned what the weight-goal card already
 *    says. Only when changed, because the field is prefilled from a reading
 *    that may be weeks old, and writing it back unconditionally would stamp a
 *    measurement nobody took as today's weigh-in. The goal still gets its
 *    starting point either way: the server takes the latest entry whatever its
 *    date.
 * 3. **`PUT /v1/goal`.**
 * 4. **`PUT /v1/targets`, last, with `onDone()` on the next line.** Setting the
 *    budget invalidates `["overview"]`, and the gate that renders this screen
 *    is `logged_days === 0 && target_kcal === null`. The moment that refetch
 *    lands the gate flips and this component unmounts mid-sequence; any `await`
 *    after it resolves into a dead closure, so a failing goal write would set
 *    an error on something nobody is looking at and the user would arrive in
 *    the app with a budget, no goal, and no explanation. Last makes the race
 *    impossible rather than unlikely.
 *
 * Everything is checked before the first request, so a rejection writes
 * nothing: half a profile saved and an error the user cannot clear is how this
 * screen used to end, with Skip as the only exit.
 *
 * And every write is idempotent — a `PATCH` of changed keys, one weigh-in per
 * day, a goal that replaces the goal, a target effective-dated to today — which
 * is why a failure keeps the user here with their typed values and an invitation
 * to press again, rather than bouncing them somewhere to start over.
 */

import {
  ageOn,
  deviceTimeZone,
  estimateBudget,
  earliestGoalDate,
  localDate,
  parseDecimal,
  plannedRate,
  useRecordWeight,
  useSetDailyTarget,
  useSetGoal,
  useUpdateProfile,
  validateWeightGoal,
  type Profile,
  type ProfileUpdate,
} from "@calorio/core";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCalorio } from "../app/providers";
import { EstimatedBudget } from "../components/EstimatedBudget";

const ACTIVITY: { value: NonNullable<Profile["activity_level"]>; label: string }[] = [
  { value: "sedentary", label: "Sedentary — desk job, little exercise" },
  { value: "light", label: "Light — 1–2 sessions a week" },
  { value: "moderate", label: "Moderate — 3–4 sessions a week" },
  { value: "active", label: "Active — 5–6 sessions a week" },
  { value: "very_active", label: "Very active — daily training or manual work" },
];

interface FirstRunProps {
  profile: Profile | undefined;
  onDone: () => void;
}

export function FirstRun({ profile, onDone }: FirstRunProps) {
  const ctx = useCalorio();
  const navigate = useNavigate();
  const today = localDate(ctx.timeZone);
  const device = deviceTimeZone();

  const updateProfile = useUpdateProfile(ctx);
  const recordWeight = useRecordWeight(ctx);
  const setGoal = useSetGoal(ctx);
  const setTarget = useSetDailyTarget(ctx);

  /*
   * Deliberately *not* keyed, and deliberately not resynced from `profile`.
   *
   * This looks like an omission next to the keyed `ProfileCard` on **You**, so:
   * the writes below arrive back as a fresh profile object mid-flow —
   * `useUpdateProfile` calls `setQueryData` and `useRecordWeight` invalidates
   * the profile query — and a `key` on anything above these fields, or an
   * effect copying the prop into state, would wipe the form between step one
   * and step four. `useState` initialisers do not re-run, which is exactly the
   * behaviour this screen wants: typed input survives its own saves.
   */
  const [zone, setZone] = useState(profile?.timezone ?? device);
  // Prefilled from the profile where a weigh-in already exists — someone who
  // logged their weight before reaching this screen should not retype it.
  const [weight, setWeight] = useState(
    profile?.latest_weight_kg ? String(profile.latest_weight_kg) : "",
  );
  const [height, setHeight] = useState(profile?.height_cm ? String(profile.height_cm) : "");
  const [birthDate, setBirthDate] = useState(profile?.birth_date ?? "");
  const [targetKg, setTargetKg] = useState("");
  const [by, setBy] = useState("");
  type Activity = NonNullable<Profile["activity_level"]>;
  const [activity, setActivity] = useState<Activity>(profile?.activity_level ?? "moderate");
  const [problem, setProblem] = useState<string | null>(null);

  const weightKg = parseDecimal(weight);
  const heightCm = parseDecimal(height);
  const target = parseDecimal(targetKg);
  // From the field, not from the profile. The profile's column is null for
  // every newly invited account, which is how this estimate came to be null for
  // all of them.
  const age = birthDate ? ageOn(birthDate, today) : null;

  /**
   * The rate the target and date imply, or a gentle default.
   *
   * Half a kilo a week when no date is given: fast enough to be visible inside a
   * month, slow enough to be survivable. Someone who wants a deadline can give
   * one, and the figure below moves while they watch.
   */
  const rate = plannedRate({
    targetKg: target,
    latestWeighIn: weightKg,
    today,
    date: by,
  });

  const estimate =
    Number.isFinite(weightKg) && Number.isFinite(heightCm) && age !== null
      ? estimateBudget({
          weightKg,
          heightCm,
          ageYears: age,
          sex: (profile?.sex ?? "unspecified") as "male" | "female" | "unspecified",
          activity,
          rateKgPerWeek: rate,
        })
      : null;

  const start = async () => {
    setProblem(null);

    /*
     * Checked here, before anything is sent, with the server's own bounds and
     * the house sentences for them. The alternative is what this screen used to
     * do: patch the profile, then take a 400 from the goal endpoint, leaving
     * half the form saved and the raw server string on screen.
     */
    if (weight.trim() && (!Number.isFinite(weightKg) || weightKg <= 0 || weightKg >= 700))
      return setProblem("Weight must be a number in kilograms, under 700.");

    if (height.trim() && (!Number.isFinite(heightCm) || heightCm <= 0 || heightCm >= 300))
      return setProblem("Height must be a number in centimetres, under 300.");

    if (birthDate.trim() && age === null)
      return setProblem("That date of birth doesn't give an age — check the year.");

    /*
     * A goal needs a starting point, and the server snapshots it from the
     * latest weigh-in. Step two below supplies one when a weight was typed, so
     * the only failing case left is a target with no weight anywhere — and that
     * gets the same sentence the weight-goal card gives, pointing up this
     * screen rather than at another page, because the field it wants is here.
     */
    let goalBody: Parameters<typeof setGoal.mutateAsync>[0] | null = null;
    if (targetKg.trim()) {
      const startPoint = Number.isFinite(weightKg) ? weightKg : profile?.latest_weight_kg ?? null;
      if (startPoint === null)
        return setProblem("A goal needs a starting point. Fill in your weight above first.");

      const checked = validateWeightGoal({
        target: targetKg,
        date: by,
        rate: "",
        latestWeighIn: startPoint,
        today,
      });
      if (!checked.ok) return setProblem(checked.problem);
      goalBody = checked.value;
    }

    // Named so a failure can say which step it was, which is the difference
    // between "try again" and knowing what was already written.
    let doing = "save your profile";
    try {
      // One patch of changed keys, built field by field the way `ProfileCard`
      // builds it — a blanked field is a `null`, an untouched one is absent.
      const changes: ProfileUpdate = {};
      if (zone !== profile?.timezone) changes.timezone = zone;
      if (activity !== profile?.activity_level) changes.activity_level = activity;

      const storedHeight = profile?.height_cm ? String(profile.height_cm) : "";
      if (height !== storedHeight) changes.height_cm = height.trim() ? heightCm : null;

      const storedBirth = profile?.birth_date ?? "";
      if (birthDate !== storedBirth) changes.birth_date = birthDate || null;

      if (Object.keys(changes).length) await updateProfile.mutateAsync(changes);

      // Before the goal, and only when it moved. See the header.
      doing = "record your weight";
      if (Number.isFinite(weightKg) && weightKg !== profile?.latest_weight_kg)
        await recordWeight.mutateAsync({ weight_kg: weightKg });

      doing = "save your goal";
      if (goalBody) await setGoal.mutateAsync(goalBody);

      // Last, because this one unmounts the screen. Nothing may be awaited
      // after it.
      doing = "set your budget";
      if (estimate)
        await setTarget.mutateAsync({
          kcal: estimate.budgetKcal,
          protein_g: estimate.protein_g,
          carbs_g: estimate.carbs_g,
          fat_g: estimate.fat_g,
        });
      onDone();
      navigate("/");
    } catch (error) {
      setProblem(
        `Couldn't ${doing}. ${error instanceof Error ? error.message : "Something went wrong."} ` +
          "Your answers are still here — pressing Start tracking again is safe.",
      );
    }
  };

  // The weigh-in counts: without it there is a window between the patch landing
  // and the weight request starting in which the button is live again.
  const saving =
    updateProfile.isPending ||
    recordWeight.isPending ||
    setGoal.isPending ||
    setTarget.isPending;

  return (
    <div className="firstrun">
      <h1 className="firstrun__mark">Calorio</h1>
      <p className="firstrun__tagline">
        Photograph a label, get the numbers. Two minutes of setup and the rest is scanning.
      </p>

      {/* First, and not in settings. A wrong zone files every evening meal on
          the following day, forever, and this is the only moment the app can
          catch it before any entries exist to be filed wrongly. */}
      <div className="firstrun__zone">
        <span>
          Timezone <strong>{zone}</strong> — today is{" "}
          <span className="num">
            {new Date(`${today}T12:00:00Z`).toLocaleDateString(undefined, {
              day: "numeric",
              month: "short",
            })}
          </span>
        </span>
        {zone !== device && (
          <button className="firstrun__change" onClick={() => setZone(device)}>
            Use {device}
          </button>
        )}
      </div>

      <div className="firstrun__grid">
        <label className="firstrun__field">
          <span>Weight now, kg</span>
          <input
            className="field num"
            inputMode="decimal"
            value={weight}
            onChange={(event) => setWeight(event.target.value)}
            aria-label="Weight now in kilograms"
          />
        </label>
        <label className="firstrun__field">
          <span>Height, cm</span>
          <input
            className="field num"
            inputMode="decimal"
            value={height}
            onChange={(event) => setHeight(event.target.value)}
            aria-label="Height in centimetres"
          />
        </label>
      </div>

      {/* The third estimator input, and the one that used to be a sentence
          pointing at a page this screen cannot reach. */}
      <label className="firstrun__field">
        <span>Date of birth</span>
        <input
          className="field num firstrun__date"
          type="date"
          value={birthDate}
          max={today}
          onChange={(event) => setBirthDate(event.target.value)}
          aria-label="Date of birth"
        />
        <span className="stat__label">
          Used for the resting-energy estimate, and nothing else.
        </span>
        {birthDate && age === null && (
          <span className="stat__label" style={{ color: "var(--alert-text)" }}>
            That date doesn&rsquo;t give an age — check the year.
          </span>
        )}
      </label>

      <label className="firstrun__field">
        <span>Activity</span>
        <select
          className="field"
          value={activity}
          onChange={(event) => setActivity(event.target.value as Activity)}
          aria-label="Activity level"
        >
          {ACTIVITY.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      {/* After the number, so typing here moves a figure already on screen. */}
      <div className="firstrun__grid">
        <label className="firstrun__field">
          <span>Target, kg</span>
          <input
            className="field num"
            inputMode="decimal"
            value={targetKg}
            onChange={(event) => setTargetKg(event.target.value)}
            aria-label="Target weight in kilograms"
          />
        </label>
        <label className="firstrun__field">
          <span>By</span>
          <input
            className="field num"
            type="date"
            value={by}
            min={earliestGoalDate(today)}
            onChange={(event) => setBy(event.target.value)}
            aria-label="Target date"
          />
        </label>
      </div>

      {/* The point of the screen: the number, arrived at in front of them, with
          its own uncertainty stated rather than implied. */}
      {estimate && (
        <EstimatedBudget
          estimate={estimate}
          footnote="Adjustable any time on You. Nothing here is locked in."
        />
      )}

      {problem && (
        <p className="error" style={{ margin: 0 }}>
          {problem}
        </p>
      )}

      <button className="firstrun__go" onClick={() => void start()} disabled={saving}>
        {saving ? "Saving…" : "Start tracking"}
      </button>

      {/* A real exit, not a postponement. Someone who wants a calorie count and
          nothing else should not be held at a form about their height. */}
      <button className="firstrun__skip" onClick={onDone}>
        Skip — just count calories
      </button>
    </div>
  );
}
